import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { nlpIntegration } from '@mi-front/api-clients';
import { useQueryClient } from '@tanstack/react-query';
import debugFn from 'debug';
import { useCallback, useEffect, useRef, useState } from 'react';

import { PANEL_REFETCH_INTERVAL_MS, TOPICS_LIMIT } from '../consts';
import { formatLiveTopicsQueryDatetime } from '../live-topics-panel-utils';

const debug = debugFn('useLiveTopicsPaginatedData');

/**
 * Deduplicates topics by ID (first occurrence wins) and sorts descending by start_time.
 */
function mergeAndDedupe(topics: nlpIntegration.TopicOutDto[]): nlpIntegration.TopicOutDto[] {
  const seen = new Map<string, nlpIntegration.TopicOutDto>();

  for (const t of topics) {
    if (!seen.has(t.id)) {
      seen.set(t.id, t);
    }
  }

  return [...seen.values()].sort(
    (a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime(),
  );
}

/**
 * Paginated data hook for the Live Topics panel.
 *
 * - **Initial load**: fetches N topics up to now (desc by start_time).
 * - **Polling**: every PANEL_REFETCH_INTERVAL_MS, fetches topics newer than the newest loaded
 *   and prepends them.
 * - **Load more**: on demand, fetches older topics before the oldest loaded topic.
 *
 * All results are merged and deduplicated by topic ID.
 * When polling detects new arrivals, the track's time-range query is invalidated
 * so it refetches sooner.
 */
export function useLiveTopicsPaginatedData() {
  const { slug } = useActiveStation();
  const queryClient = useQueryClient();

  const [topics, setTopics] = useState<nlpIntegration.TopicOutDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreTopicIds, setLoadMoreTopicIds] = useState<Set<string>>(() => new Set());

  const topicsRef = useRef(topics);
  topicsRef.current = topics;

  // Upper-bound timestamp captured at mount, used for the initial query and
  // as polling fallback when no topics are loaded yet.
  const initialTimestampRef = useRef(new Date().toISOString());

  // --- Initial fetch (re-runs when slug changes) ---
  useEffect(() => {
    setTopics([]);
    setHasMoreOlder(true);
    setIsLoadingMore(false);
    setIsSuccess(false);
    setLoadMoreTopicIds(new Set());
    initialTimestampRef.current = new Date().toISOString();

    if (!slug) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const fetchInitial = async () => {
      try {
        const data = await nlpIntegration.queryTopicsLiveTopics(slug, {
          to_datetime: initialTimestampRef.current,
          order: 'desc',
          limit: TOPICS_LIMIT,
        });
        if (cancelled) return;
        debug('Initial load: %d topics', data.length);
        setTopics(data);
        setHasMoreOlder(data.length >= TOPICS_LIMIT);
        setIsSuccess(true);
      } catch (err) {
        debug('Initial load error:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchInitial();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // --- Polling for new topics ---
  useEffect(() => {
    if (!slug || !isSuccess) return;

    const poll = async () => {
      const current = topicsRef.current;
      const fromDatetime = formatLiveTopicsQueryDatetime(
        current.length > 0 ? current[0].start_time : initialTimestampRef.current,
      );

      try {
        const data = await nlpIntegration.queryTopicsLiveTopics(slug, {
          from_datetime: fromDatetime,
          order: 'desc',
        });

        if (data.length === 0) return;

        const existingIds = new Set(current.map((t) => t.id));
        const hasNewTopics = data.some((t) => !existingIds.has(t.id));

        if (hasNewTopics) {
          debug('Poll: %d topics fetched, new arrivals found', data.length);
          setTopics((prev) => mergeAndDedupe([...data, ...prev]));

          queryClient.invalidateQueries({
            queryKey: nlpIntegration.queryTopicsLiveTopicsQueryKey(slug),
          });
        }
      } catch (err) {
        debug('Poll error:', err);
      }
    };

    const interval = setInterval(poll, PANEL_REFETCH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [slug, isSuccess, queryClient]);

  // --- Load more older topics ---
  const loadMoreOlder = useCallback(async () => {
    if (!slug || isLoadingMore || !hasMoreOlder) return;

    const current = topicsRef.current;
    const oldestTopic = current[current.length - 1];
    if (!oldestTopic) return;

    setIsLoadingMore(true);
    try {
      const data = await nlpIntegration.queryTopicsLiveTopics(slug, {
        to_datetime: formatLiveTopicsQueryDatetime(oldestTopic.start_time),
        order: 'desc',
        limit: TOPICS_LIMIT,
      });

      debug('Load more: %d older topics', data.length);

      setLoadMoreTopicIds((prev) => {
        const next = new Set(prev);
        data.forEach((t) => next.add(t.id));
        return next;
      });
      setTopics((prev) => mergeAndDedupe([...prev, ...data]));
      setHasMoreOlder(data.length >= TOPICS_LIMIT);
    } catch (err) {
      debug('Load more error:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [slug, isLoadingMore, hasMoreOlder]);

  return {
    slug,
    topics,
    isLoading,
    isSuccess,
    isLoadingMore,
    hasMoreOlder,
    loadMoreOlder,
    /** IDs of topics loaded via "load more" — used to exclude them from "new arrival" detection. */
    loadMoreTopicIds,
  };
}
